// Time without further clicks after which the in-flight smooth scroll is
// assumed settled and the tracked target is dropped. ~700ms covers
// Chrome/Safari/Firefox native smooth-scroll durations.
const SCROLL_TARGET_TTL_MS = 700;

// A horizontally-scrollable container that:
//   - hides its prev/next nav buttons when there's nothing to scroll to in
//     that direction;
//   - lets rapid arrow clicks accumulate against the in-flight smooth-scroll
//     target rather than the still-animating scrollLeft;
//   - advances one snap point at a time, accommodating variable slide widths.
class ScrollCarousel {
  private target: number | null = null;
  private targetTimer: number | null = null;

  constructor(
    private readonly container: HTMLElement,
    private readonly prev: HTMLButtonElement | null,
    private readonly next: HTMLButtonElement | null
  ) {
    prev?.addEventListener('click', () => this.advance(-1));
    next?.addEventListener('click', () => this.advance(1));

    const refresh = () => this.refreshNav();
    container.addEventListener('scroll', refresh, { passive: true });
    if ('ResizeObserver' in window) new ResizeObserver(refresh).observe(container);
    // Re-check after window load: images without dimension attrs can shift
    // scrollWidth on first paint.
    window.addEventListener('load', refresh, { once: true });
    refresh();
  }

  refreshNav(): void {
    const max = this.container.scrollWidth - this.container.clientWidth;
    const left = this.container.scrollLeft;
    if (this.prev) this.prev.hidden = left <= 1;
    if (this.next) this.next.hidden = left >= max - 1;
  }

  private advance(direction: number): void {
    const base = this.target ?? this.container.scrollLeft;
    const dest = this.findNeighbor(base, direction);
    if (dest === null) return;
    // Mid-flight click jumps instantly to the new destination — interrupting
    // a smooth scroll with another smooth scroll resets the easing curve and
    // looks jolty. Once the previous animation has settled, we ease again.
    const interrupting = this.target !== null;
    this.target = dest;
    this.container.scrollTo({ left: dest, behavior: interrupting ? 'auto' : 'smooth' });
    if (this.targetTimer !== null) clearTimeout(this.targetTimer);
    this.targetTimer = window.setTimeout(() => {
      this.target = null;
      this.targetTimer = null;
    }, SCROLL_TARGET_TTL_MS);
  }

  // Find the slide currently centered against `scrollLeft` and return the
  // scrollLeft that would center its `direction`-th neighbor. Variable widths
  // mean we can't just step by clientWidth.
  private findNeighbor(scrollLeft: number, direction: number): number | null {
    const slides = this.container.children;
    if (slides.length === 0) return null;

    const viewportCenter = scrollLeft + this.container.clientWidth / 2;
    let activeIdx = 0;
    let minDist = Infinity;
    for (let i = 0; i < slides.length; i++) {
      const slide = slides[i] as HTMLElement;
      const slideCenter = slide.offsetLeft + slide.offsetWidth / 2;
      const dist = Math.abs(slideCenter - viewportCenter);
      if (dist < minDist) {
        minDist = dist;
        activeIdx = i;
      }
    }

    const target = slides[Math.max(0, Math.min(slides.length - 1, activeIdx + direction))] as HTMLElement;
    const max = this.container.scrollWidth - this.container.clientWidth;
    const desired = target.offsetLeft + target.offsetWidth / 2 - this.container.clientWidth / 2;
    return Math.max(0, Math.min(max, desired));
  }
}

class InUseGallery {
  private readonly source: HTMLElement | null;
  private readonly lightbox: HTMLDialogElement;
  private readonly track: HTMLElement;
  private readonly lightboxCarousel: ScrollCarousel;
  private savedScrollY = 0;

  constructor(root: HTMLElement) {
    const lightbox = root.querySelector<HTMLDialogElement>('[data-in-use-lightbox]');
    const track = lightbox?.querySelector<HTMLElement>('[data-in-use-lightbox-track]') ?? null;
    if (!lightbox || !track) {
      throw new Error(
        '[in-use-gallery] requires a [data-in-use-lightbox] dialog with a [data-in-use-lightbox-track] inside.'
      );
    }

    this.source = root.querySelector<HTMLElement>('[data-in-use-source]');
    this.lightbox = lightbox;
    this.track = track;

    if (this.source) {
      new ScrollCarousel(
        this.source,
        root.querySelector<HTMLButtonElement>('[data-in-use-source-prev]'),
        root.querySelector<HTMLButtonElement>('[data-in-use-source-next]')
      );
    }
    this.lightboxCarousel = new ScrollCarousel(
      track,
      lightbox.querySelector<HTMLButtonElement>('[data-in-use-prev]'),
      lightbox.querySelector<HTMLButtonElement>('[data-in-use-next]')
    );

    this.bindEvents();
  }

  private bindEvents(): void {
    this.source?.addEventListener('click', (event) => {
      const slide = (event.target as HTMLElement | null)?.closest<HTMLElement>('[data-in-use-index]');
      if (!slide) return;
      const index = Number(slide.dataset.inUseIndex);
      if (Number.isFinite(index)) this.openAt(index);
    });

    // Close on any click that isn't on interactive content (info text/links
    // or nav arrows). Backdrop clicks land on the dialog element itself; the
    // close button has no early-return, so its bubbled click closes too.
    this.lightbox.addEventListener('click', (event) => {
      const target = event.target as HTMLElement | null;
      if (!target) return;
      if (target.closest('.in-use-lightbox__info, [data-in-use-prev], [data-in-use-next]')) return;
      this.lightbox.close();
    });

    // ESC dispatches `cancel` followed by `close` (cancel isn't prevented),
    // so handling `close` is enough for both backdrop clicks and ESC.
    this.lightbox.addEventListener('close', () => {
      document.body.classList.remove('in-use-lightbox-open');
      document.body.style.top = '';
      window.scrollTo(0, this.savedScrollY);
    });
  }

  openAt(index: number): void {
    const slide = this.track.children[index] as HTMLElement | undefined;
    if (!slide) return;

    // Body scroll lock: pin the body using `position: fixed` and an explicit
    // top offset so the visual scroll position is preserved.
    this.savedScrollY = window.scrollY;
    document.body.style.top = `-${this.savedScrollY}px`;
    document.body.classList.add('in-use-lightbox-open');

    if (!this.lightbox.open) this.lightbox.showModal();

    // Defer one frame so the dialog is laid out in the top layer before we
    // measure. Slide width equals track width (100vw), so offsetLeft is the
    // scrollLeft that centers the slide.
    requestAnimationFrame(() => {
      this.track.scrollLeft = slide.offsetLeft;
      this.lightboxCarousel.refreshNav();
    });
  }
}

export function initInUseGalleries(root: ParentNode = document): InUseGallery[] {
  return Array.from(root.querySelectorAll<HTMLElement>('[data-in-use-gallery]')).map(
    (el) => new InUseGallery(el)
  );
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initInUseGalleries());
} else {
  initInUseGalleries();
}
